/**
 * CharaKarakaSection - Registration for D7 Chara Karaka table in enhanced plugin system
 * Demonstrates migration from DIVISIONAL_CHARTS to plugin architecture
 */

import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { enhancedRegistry } from '../registry/EnhancedSectionRegistry';
import { CharaKarakaInfoTable } from '../components/CharaKarakaInfoTable';
import { JatakaResponse } from '../../../../../models/api';

// Import calculator to ensure it's registered
import '../calculators/CharaKarakaCalculator';

/**
 * Custom loading component for D7 Chara Karaka
 */
const CharaKarakaLoadingSpinner: React.FC<{ progress?: number }> = ({ progress = 0 }) => {
    return (
        <div style={{
            padding: '20px',
            textAlign: 'center',
            background: '#f0f8ff',
            borderRadius: '8px',
            border: '1px solid #d1ecf1'
        }}>
            <div style={{
                fontSize: '14px',
                color: '#0c5460',
                marginBottom: '12px'
            }}>
                🎯 Calculating D7 Chara Karaka...
            </div>
            {progress > 0 && (
                <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: '#bee5eb',
                    borderRadius: '2px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                }}>
                    <div style={{
                        width: `${progress}%`,
                        height: '100%',
                        backgroundColor: '#17a2b8',
                        transition: 'width 0.3s ease'
                    }} />
                </div>
            )}
            <div style={{
                fontSize: '12px',
                color: '#6c757d'
            }}>
                {progress > 0 ? `${Math.round(progress)}% complete` : 'Analyzing significators...'}
            </div>
        </div>
    );
};

// Register the enhanced section
enhancedRegistry.register({
    id: 'chara-karaka-d7',
    parentSection: 'Divisional',
    label: 'D7 Chara Karaka',
    component: CharaKarakaInfoTable,
    type: 'table',
    order: 10, // First in divisional charts

    // Lazy loading with D7 calculation
    lazy: {
        calculator: 'chara-karaka-d7',
        loader: CharaKarakaLoadingSpinner,
        dependencies: ['graha_positions.planets']
    },

    // State persistence for table
    persistence: {
        enabled: true,
        stateKeys: ['expanded', 'sortOrder'],
        namespace: 'chara-karaka-d7'
    },

    // Event handlers for D7 monitoring
    events: {
        onMount: (apiResponse: JatakaResponse) => {
            devLog(`🎯 CharaKarakaSection: Mounted D7 with ${apiResponse.graha_positions?.planets?.length || 0} planets`);
        },

        onExpand: (isExpanded: boolean) => {
            devLog(`📊 CharaKarakaSection: D7 ${isExpanded ? 'Expanded' : 'Collapsed'}`);
        },

        onCalculationStart: (calculatorId: string) => {
            devLog(`⚡ CharaKarakaSection: Starting D7 calculation with "${calculatorId}"`);
        },

        onCalculationComplete: (result: any, calculatorId: string) => {
            devLog(`✅ CharaKarakaSection: D7 calculation complete!`);
            devLog(`   Calculator: ${calculatorId}`);
            devLog(`   Significators calculated: ${result.significators?.length || 0}`);
            devLog(`   Calculation time: ${result.calculationTime}ms`);

            // Log significator summary
            if (result.significators) {
                result.significators.forEach((sig: any, index: number) => {
                    devLog(`   ${index + 1}. ${sig.name}: ${sig.planet}`);
                });
            }
        },

        onCalculationError: (error: Error, calculatorId: string) => {
            devError(`❌ CharaKarakaSection: D7 calculation error in "${calculatorId}":`, error);
        }
    },

    // Only show if we have planet data
    condition: (apiResponse: JatakaResponse) => {
        const hasPlants = apiResponse.graha_positions?.planets && apiResponse.graha_positions.planets.length >= 7;

        if (!hasPlants) {
            devWarn(`⚠️ CharaKarakaSection: Need at least 7 planets for D7 calculation, hiding section`);
        }

        return !!hasPlants;
    },

    // Component props
    componentProps: {
        showRankings: true,
        enableSorting: true,
        showDescriptions: true
    }
});

// Export for backward compatibility
export { CharaKarakaInfoTable };
export default CharaKarakaInfoTable;